/**
 * TanStack Query hook for fetching Slide Sense slides with infinite scroll.
 */

import { useInfiniteQuery } from "@tanstack/react-query"

import type { SlideSenseSlide } from "../types"

import { fetchSlides } from "../api"

const PAGE_SIZE = 20

/**
 * Fetch slides with infinite scroll pagination.
 * Returns pages of 20 slides, loads more when `fetchNextPage` is called.
 *
 * Pass `enabled: false` to gate the query — used by DeckSection so we
 * don't fetch slides for collapsed decks.
 */
export function useSlides(params: {
  folderId?: string
  deckId?: string
  enabled?: boolean
  folderIds?: string[]
  collectionIds?: string[]
}) {
  const { folderId, deckId, enabled = true, folderIds, collectionIds } = params
  const folderIdsKey = folderIds && folderIds.length > 0 ? folderIds.join(",") : undefined
  const collectionIdsKey =
    collectionIds && collectionIds.length > 0 ? collectionIds.join(",") : undefined
  return useInfiniteQuery<SlideSenseSlide[]>({
    queryKey: ["slide-sense", "slides", { folderId, deckId, folderIdsKey, collectionIdsKey }],
    queryFn: ({ pageParam = 0 }) =>
      fetchSlides({
        folder_id: folderId,
        deck_id: deckId,
        page: pageParam as number,
        page_size: PAGE_SIZE,
        folder_ids: folderIdsKey,
        collection_ids: collectionIdsKey,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      // If we got fewer than PAGE_SIZE results, there are no more pages
      if (lastPage.length < PAGE_SIZE) return undefined
      return (lastPageParam as number) + 1
    },
    enabled,
  })
}
